import { NewsArticlePromotion } from '@models/news-article';
import { patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { findAndRemove } from '../../helpers';
import { NewsArticlesService } from '../news-articles.service';
import { addDataToListItem, findAndReplaceByIdWithSort, NewsArticlesStore } from '../news-articles.store';

export const getNewsArticlePromotionHandlingMethods = (injects: { store: NewsArticlesStore; newsArticlesService: NewsArticlesService }) => {
  const { store, newsArticlesService } = injects;

  const addNewsArticlePromotion = rxMethod<{ articleId: string; promotion: any }>(
    pipe(
      tap(() => patchState(store, { isSelectedNewsArticleLoading: true, isLoading: true })),
      switchMap(({ articleId, promotion }) =>
        newsArticlesService.addPromotion(articleId, promotion).pipe(
          tap(newsArticle => {
            patchState(store, {
              selectedNewsArticle: addDataToListItem(newsArticle, store.selectedNewsArticle()),
              newsArticles: findAndReplaceByIdWithSort(store.newsArticles(), newsArticle),
              newsArticlesByCommunity: findAndReplaceByIdWithSort(store.newsArticlesByCommunity(), newsArticle),
              isSelectedNewsArticleLoading: false,
              isLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, { isSelectedNewsArticleLoading: false, isLoading: false });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  const updateNewsArticlePromotion = rxMethod<{ articleId: string; communityId: string; promotion: any }>(
    pipe(
      tap(() => patchState(store, { isSelectedNewsArticleLoading: true, isLoading: true })),
      switchMap(({ articleId, communityId, promotion }) =>
        newsArticlesService.updatePromotion(articleId, communityId, promotion).pipe(
          tap(newsArticle => {
            patchState(store, {
              selectedNewsArticle: addDataToListItem(newsArticle, store.selectedNewsArticle()),
              newsArticles: findAndReplaceByIdWithSort(store.newsArticles(), newsArticle),
              newsArticlesByCommunity: findAndReplaceByIdWithSort(store.newsArticlesByCommunity(), newsArticle),
              isSelectedNewsArticleLoading: false,
              isLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, { isSelectedNewsArticleLoading: false, isLoading: false });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  const deleteNewsArticlePromotion = rxMethod<{ articleId: string; communityId: string }>(
    pipe(
      tap(() => patchState(store, { isSelectedNewsArticleLoading: true })),
      switchMap(({ articleId, communityId }) =>
        newsArticlesService.deletePromotion(articleId, communityId).pipe(
          tap(() => {
            const selected = store.selectedNewsArticle();
            const updatedPromotions = findAndRemove<NewsArticlePromotion>(
              selected?.promotions ?? [],
              promotion => promotion.communityId === communityId,
            );
            const updatedArticle = { ...selected!, promotions: updatedPromotions };
            patchState(store, {
              selectedNewsArticle: updatedArticle,
              newsArticles: findAndReplaceByIdWithSort(store.newsArticles(), updatedArticle),
              newsArticlesByCommunity: findAndReplaceByIdWithSort(store.newsArticlesByCommunity(), updatedArticle),
              isSelectedNewsArticleLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, { isSelectedNewsArticleLoading: false });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  return {
    addNewsArticlePromotion,
    updateNewsArticlePromotion,
    deleteNewsArticlePromotion,
  };
};

export interface NewsArticlePromotionHandlingStoreType {
  readonly addNewsArticlePromotion: (input: { articleId: string; promotion: any }) => void;
  readonly updateNewsArticlePromotion: (input: { articleId: string; communityId: string; promotion: any }) => void;
  readonly deleteNewsArticlePromotion: (input: { articleId: string; communityId: string }) => void;
}
